/**
 * First-party frontend error reporting (CEL-1117, Arjun 2026-08-11).
 *
 * Captures uncaught window errors, unhandled promise rejections, and React
 * render errors (via RootErrorBoundary) and ships them to our OWN backend:
 *
 *   POST /api/cellcog/fe-errors  →  [FEERR] journald line  →  Cloud Logging
 *   →  agent_docs/log_health_dashboard/fetch_fe_errors.py  →  Log Health board
 *
 * First-party BY DESIGN (build-vs-buy decision with Nitish, 2026-08-11): the
 * users we most need to see are the ad-blocker crowd (the /signup leak
 * lesson), and blockers kill vendor telemetry hosts (Sentry/PostHog) the same
 * way they killed identitytoolkit.googleapis.com. A same-origin /api/* call
 * passes blockers and rides the existing CEL-1094 observability pipeline.
 *
 * Safety properties (an error reporter must never make things worse):
 *   - Hard cap per session (MAX_PER_SESSION) — an error loop can't flood us.
 *   - Per-signature dedupe — each distinct error reports once per page load.
 *   - Batched + debounced; sendBeacon (fire-and-forget, survives unload) with
 *     a keepalive-fetch fallback.
 *   - Every entry point is try/caught — the reporter itself can never throw.
 *   - Noise dropped at the source: cross-origin "Script error.", browser
 *     extension frames, ResizeObserver loop warnings, react-snap prerender.
 */

const ENDPOINT = '/api/cellcog/fe-errors';
const MAX_PER_SESSION = 10;
const FLUSH_DELAY_MS = 2000;
const MAX_MSG = 300;
const MAX_STACK = 1200;

let reportedCount = 0;
const seenSignatures = new Set();
let queue = [];
let flushTimer = null;
let initialized = false;
let cachedBundle = null;

/** The deployed bundle filename (e.g. main.abc123.js) — lets us pick the right
 *  sourcemap offline when symbolicating a minified stack, and gives the
 *  dashboard a "since which deploy" axis. */
function getBundleName() {
  if (cachedBundle !== null) return cachedBundle;
  cachedBundle = '';
  try {
    const scripts = document.querySelectorAll('script[src]');
    for (let i = 0; i < scripts.length; i++) {
      const m = scripts[i].src.match(/(main\.[^/]+\.js)/);
      if (m) {
        cachedBundle = m[1];
        break;
      }
    }
  } catch (e) {
    /* never throw from the reporter */
  }
  return cachedBundle;
}

/** djb2 over message + first meaningful stack frame — stable grouping key. */
function signatureOf(message, stack) {
  const firstFrame = (stack || '').split('\n').slice(0, 2).join(' ');
  const str = `${message}|${firstFrame}`;
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h.toString(16);
}

function isNoise(message, stack) {
  if (!message) return true;
  // Cross-origin scripts report a bare "Script error." with zero information.
  if (message === 'Script error.') return true;
  // Benign, browser-generated, famously noisy.
  if (/ResizeObserver loop/i.test(message)) return true;
  // Errors originating inside browser extensions are not our bugs.
  if (/(chrome|moz|safari)-extension:\/\//.test(stack || '') ||
      /(chrome|moz|safari)-extension:\/\//.test(message)) return true;
  // WebKit's own media-controls internals leak `EmptyRanges` as a ReferenceError on <video> pages
  // (Safari bug, fires from the shadow DOM controller — not our code). 11 hits/14d on /super-agents.
  if (/EmptyRanges/.test(message)) return true;
  // Autoplay policy: the browser refused video.play() without a user gesture — expected, the poster
  // frame covers it. Reporting it only teaches the panel to ignore rejections.
  if (/The play method is not allowed|play\(\) request was interrupted|NotAllowedError/.test(message)) return true;
  // Safari's bare network-failure TypeError for fetch/media ("Load failed", no stack) — environmental.
  if (/^(TypeError: )?Load failed$/.test(message)) return true;
  return false;
}

function isPrerender() {
  try {
    return typeof navigator !== 'undefined' && /ReactSnap/.test(navigator.userAgent);
  } catch (e) {
    return false;
  }
}

function flushNow() {
  try {
    if (!queue.length) return;
    const payload = JSON.stringify({ errors: queue });
    queue = [];
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    // sendBeacon: fire-and-forget, survives page unload, never blocks the UI.
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    // Fallback (older browsers / beacon refused): keepalive fetch, errors ignored.
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* never throw from the reporter */
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushNow();
  }, FLUSH_DELAY_MS);
}

/**
 * Report one frontend error. Exported for RootErrorBoundary; window listeners
 * use it internally.
 * @param {string} kind - 'window_error' | 'unhandled_rejection' | 'react_boundary'
 * @param {string} message
 * @param {string} stack
 */
export function reportFeError(kind, message, stack) {
  try {
    if (isPrerender()) return;
    const msg = String(message || '').slice(0, MAX_MSG);
    const stk = String(stack || '').slice(0, MAX_STACK);
    if (isNoise(msg, stk)) return;
    if (reportedCount >= MAX_PER_SESSION) return;
    const sig = signatureOf(msg, stk);
    if (seenSignatures.has(sig)) return;
    seenSignatures.add(sig);
    reportedCount += 1;
    queue.push({
      kind: String(kind || 'unknown').slice(0, 40),
      sig,
      msg,
      stack: stk,
      route: String((window.location && window.location.pathname) || '').slice(0, 200),
      bundle: getBundleName(),
    });
    scheduleFlush();
  } catch (e) {
    /* never throw from the reporter */
  }
}

/** Install the global listeners. Call once, as early as possible in boot. */
export function initFeErrorReporting() {
  try {
    if (initialized || typeof window === 'undefined' || isPrerender()) return;
    initialized = true;

    // Uncaught runtime errors. Deliberately NOT capture-phase: capture:true
    // would also surface resource-load errors (img/script 404s) — noise.
    window.addEventListener('error', (event) => {
      try {
        if (!event) return;
        const msg = event.message || (event.error && event.error.message) || '';
        const stack =
          (event.error && event.error.stack) ||
          `${event.filename || ''}:${event.lineno || ''}:${event.colno || ''}`;
        reportFeError('window_error', msg, stack);
      } catch (e) {
        /* never throw from the reporter */
      }
    });

    window.addEventListener('unhandledrejection', (event) => {
      try {
        const reason = event && event.reason;
        const msg =
          (reason && (reason.message || (typeof reason === 'string' ? reason : ''))) ||
          'unhandled rejection (no message)';
        const stack = (reason && reason.stack) || '';
        reportFeError('unhandled_rejection', msg, stack);
      } catch (e) {
        /* never throw from the reporter */
      }
    });

    // Drain anything still queued when the page goes away.
    window.addEventListener('pagehide', flushNow);
  } catch (e) {
    /* never throw from the reporter */
  }
}
